import { Controller } from "@hotwired/stimulus";
import { useClickOutside } from "stimulus-use";

// Re-implémentation de la logique de select-box avec tags (type Preline)
// en pur Stimulus, sans dépendance externe.
export default class extends Controller {
  static targets = [
    "hiddenSelect", // Le <select multiple> original et caché
    "wrapper", // Le conteneur principal visible
    "tagsContainer", // La zone où les tags apparaissent
    "input", // Le champ de saisie pour la recherche
    "dropdown", // Le menu déroulant avec les options
    "option", // Chaque option cliquable dans le dropdown
  ];

  static values = {
    placeholder: String,
    tagsItemTemplate: String,
    optionTemplate: String,
  };

  connect() {
    // Utilise stimulus-use pour fermer le dropdown si on clique à l'extérieur
    if (this.hasDropdownTarget) {
      useClickOutside(this, { element: this.dropdownTarget });
    }

    // Rendre les options initialement sélectionnées
    this.renderInitialSelection();

    // Surveiller les changements sur le select caché (utile pour des mises à jour programmatiques)
    if (this.hasHiddenSelectTarget) {
      this.hiddenSelectTarget.addEventListener(
        "change",
        this.renderInitialSelection.bind(this)
      );
    }
  }

  // Action appelée quand on clique en dehors du composant
  clickOutside(event) {
    if (this.dropdownTarget.classList.contains("hidden")) return;
    this.hideDropdown();
  }

  // Affiche ou cache le dropdown
  toggleDropdown(event) {
    // Si le clic vient de l'input, on empêche l'événement de "remonter" au wrapper
    // pour éviter que la méthode soit appelée deux fois (une fois pour l'input, une fois pour le wrapper)
    // ce qui annulerait l'ouverture/fermeture du dropdown.
    if (event && this.inputTarget.contains(event.target)) {
      event.stopPropagation();
    }

    this.dropdownTarget.classList.toggle("hidden");
    if (!this.dropdownTarget.classList.contains("hidden")) {
      this.inputTarget.focus();
      // Mettre à jour les options visibles et les messages quand on ouvre le dropdown
      this.filterOptions();
    }
  }

  hideDropdown() {
    this.dropdownTarget.classList.add("hidden");
    // Nettoyer le message "aucun résultat" quand on ferme
    const existingMessage = this.dropdownTarget.querySelector('.no-results-message');
    if (existingMessage) {
      existingMessage.remove();
    }
  }

  // Gère la sélection d'une option depuis le dropdown
  selectOption(event) {
    const selectedOptionDiv = event.currentTarget;
    const { value } = selectedOptionDiv.dataset;

    const originalOption = this.findOriginalOption(value);
    if (!originalOption || originalOption.selected) return;

    // Mettre à jour le vrai <select>
    originalOption.selected = true;

    // Créer le tag visuel
    this.createTag(originalOption);

    // Cacher l'option dans le dropdown pour ne pas la re-sélectionner
    selectedOptionDiv.classList.add("hidden");

    this.inputTarget.value = "";
    this.filterOptions(); // Réinitialise le filtre
    this.hideDropdown();
    this.inputTarget.focus();
    this.updatePlaceholder();
  }

  // Gère la suppression d'un tag
  removeTag(event) {
    event.preventDefault();
    event.stopPropagation();

    const tagDiv = event.currentTarget.closest("[data-value]");
    const { value } = tagDiv.dataset;

    const originalOption = this.findOriginalOption(value);
    if (originalOption) {
      originalOption.selected = false;
    }

    // Supprimer le tag visuel
    tagDiv.remove();

    // Ré-afficher l'option dans le dropdown
    const optionDiv = this.findDropdownOption(value);
    if (optionDiv) {
      optionDiv.classList.remove("hidden");
    }
    this.updatePlaceholder();
  }

  updatePlaceholder() {
    if (!this.hasInputTarget) return;

    if (this.tagsContainerTarget.children.length > 0) {
      this.inputTarget.placeholder = "";
    } else {
      this.inputTarget.placeholder = this.placeholderValue;
    }
  }

  // Filtre les options du dropdown en fonction de la saisie
  filterOptions() {
    const query = this.inputTarget.value.toLowerCase();
    let hasVisibleOptions = false;
    
    this.optionTargets.forEach((optionEl) => {
      const isSelected = this.findOriginalOption(optionEl.dataset.value).selected;
      const title = optionEl.dataset.title.toLowerCase();
      const description = (optionEl.dataset.description || "").toLowerCase();

      // Une option est visible si elle n'est pas déjà sélectionnée ET qu'elle correspond à la recherche
      const matchesQuery = title.includes(query) || description.includes(query);
      if (!isSelected && matchesQuery) {
        optionEl.classList.remove("hidden");
        hasVisibleOptions = true;
      } else {
        optionEl.classList.add("hidden");
      }
    });

    // Gérer le message "aucun résultat"
    this.updateNoResultsMessage(query, hasVisibleOptions);
  }

  updateNoResultsMessage(query, hasVisibleOptions) {
    // Supprimer le message existant s'il y en a un
    const existingMessage = this.dropdownTarget.querySelector('.no-results-message');
    if (existingMessage) {
      existingMessage.remove();
    }

    // Si pas de résultats, afficher le message approprié
    if (!hasVisibleOptions) {
      const noResultsDiv = document.createElement('div');
      noResultsDiv.className = 'no-results-message py-2 px-4 text-sm text-gray-500 text-center';
      
      if (query.length > 0) {
        // Il y a une recherche mais pas de résultats
        noResultsDiv.textContent = `Aucun résultat pour "${query}"`;
      } else {
        // Pas de recherche mais toutes les options sont déjà sélectionnées
        noResultsDiv.textContent = "Tous les tags disponibles sont déjà sélectionnés";
      }
      
      this.dropdownTarget.appendChild(noResultsDiv);
    }
  }

  // Gère les appuis sur les touches dans l'input (ex: Backspace pour supprimer le dernier tag)
  handleKeydown(event) {
    if (event.key === "Backspace" && this.inputTarget.value === "") {
      const lastTag = this.tagsContainerTarget.querySelector("[data-value]:last-of-type");
      if (lastTag) {
        const removeButton = lastTag.querySelector("[data-action*='removeTag']");
        if (removeButton) {
          removeButton.click();
        }
      }
    }
  }

  // ========== Méthodes privées de rendu et d'aide ==========

  // Au chargement, crée les tags pour les options déjà sélectionnées
  renderInitialSelection() {
    if (!this.hasHiddenSelectTarget || !this.hasTagsContainerTarget) return;
    this.tagsContainerTarget.innerHTML = "";
    Array.from(this.hiddenSelectTarget.options).forEach((option) => {
      if (option.selected) {
        this.createTag(option);
        const dropdownOption = this.findDropdownOption(option.value);
        if (dropdownOption) dropdownOption.classList.add("hidden");
      }
    });
    this.updatePlaceholder();
  }

  // Crée un élément "tag" à partir d'une <option> et l'ajoute au conteneur
  createTag(option) {
    const { value, text } = option;
    
    // Récupérer les données depuis les data attributes de l'option correspondante dans le dropdown
    const dropdownOption = this.findDropdownOption(value);
    const icon = dropdownOption ? dropdownOption.dataset.icon || "" : "";
    const color = dropdownOption ? dropdownOption.dataset.color || "" : "";
    const description = dropdownOption ? dropdownOption.dataset.description || "" : "";

    // Décoder le HTML échappé du template
    const tempDecoder = document.createElement('div');
    tempDecoder.innerHTML = this.tagsItemTemplateValue;
    let tagHtml = tempDecoder.innerHTML;

    // Créer l'élément tag à partir du template décodé
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = tagHtml;
    const tagElement = tempDiv.firstElementChild;

    // Remplir les données dans l'élément créé
    tagElement.dataset.value = value;
    tagElement.dataset.title = text;
    tagElement.dataset.description = description;

    // Insérer l'icône dans l'élément [data-icon]
    const iconContainer = tagElement.querySelector('[data-icon]');
    if (iconContainer && icon) {
      // Décoder l'icône depuis Base64
      const decodedIcon = atob(icon);
      iconContainer.innerHTML = decodedIcon;
      
      // Appliquer la couleur si spécifiée
      if (color) {
        iconContainer.style.color = color;
      }
    } else if (iconContainer && !icon) {
      // Si pas d'icône mais que le conteneur existe, le cacher
      iconContainer.style.display = 'none';
    }

    // Insérer le texte dans l'élément [data-title]
    const titleContainer = tagElement.querySelector('[data-title]');
    if (titleContainer) {
      titleContainer.textContent = text;
    }

    // Attache l'action de suppression
    const removeButton = tagElement.querySelector("[data-remove]");
    if (removeButton) {
      removeButton.dataset.action = "click->tags-select#removeTag";
    }

    this.tagsContainerTarget.appendChild(tagElement);
  }

  // Retrouve une <option> originale dans le <select> caché par sa valeur
  findOriginalOption(value) {
    if (!this.hasHiddenSelectTarget) return null;
    return Array.from(this.hiddenSelectTarget.options).find(
      (o) => o.value === value
    );
  }

  // Retrouve un <div> d'option dans le dropdown par sa valeur
  findDropdownOption(value) {
    return this.optionTargets.find((el) => el.dataset.value === value);
  }
}